import axios from 'axios';
import getUserSession from '@utils/getSession';
import api from './api';
import { appVersion } from '../constants/config';

class ApiClient {
  constructor(apiBase) {
    this.apiBase = apiBase;
  }

  /**
   * @param {string} apiBase
   */
  setApiBase(apiBase) {
    this.apiBase = apiBase;
  }

  /**
   * Injects API methods from config
   * @param {*} metadata
   */
  injectApiMethods(metadata) {
    for (const name of Object.keys(metadata)) {
      this[name] = async (...args) => {
        const { method, url, data, transform, custom } = await metadata[name](
          ...args
        );

        if (custom) {
          return await custom(...args);
        }

        let result = await this.request(method, url, data);

        if (transform && typeof transform === 'function') {
          result = await transform(result);
        }

        return result;
      };
    }

    return this;
  }

  /**
   * Make a request to API
   * @param {string} method
   * @param {string} url
   * @param {object} options
   * @param {*} data
   */
  async request(method, url, data = undefined, options = {}) {
    const maxRetries = 3;
    const baseDelay = 1000;

    const makeRequest = async (attempt = 0) => {
      const params = {
        method,
        url,
        baseURL: this.apiBase,
        [this.dataSendType(method)]: this.serialize(data, method, options),
        ...options
      };

      try {
        const response = await axios.request({
          ...params,
          headers: await this.headers(url, options)
        });
        return response.data;
      } catch (error) {
        if (attempt >= maxRetries) {
          throw error;
        }

        const shouldRetry =
          !error.response || // Network error
          error.response.status >= 500 || // Server error
          error.response.status === 401 || // Unauthorized
          error.response.status === 429; // Rate limit

        if (!shouldRetry) {
          throw error;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));

        return makeRequest(attempt + 1);
      }
    };

    return makeRequest();
  }

  serialize(data, method, options) {
    return data &&
      ['POST', 'PUT'].includes(method.toUpperCase()) &&
      !options?.skipSerialize &&
      !options?.isFileUpload
      ? JSON.stringify(data)
      : data;
  }

  /**
   * @param {string} method
   * @returns {string}
   */
  dataSendType(method) {
    return method === 'GET' ? 'params' : 'data';
  }

  /**
   * Build request headers
   */
  async headers(url, options) {
    const headers = ApiClient.STATIC_HEADERS;
    if (options?.skipSerialize) {
      headers['Content-Type'] = 'text/plain';
    }
    if (options?.isFileUpload) {
      delete headers['Content-Type'];
    }
    const location = window.location;
    const { token, selectedCompanyId } = await getUserSession();
    headers.Authorization = token;
    headers['x-company-id'] = selectedCompanyId;
    headers['x-page'] = location.pathname + location.search + location.hash;
    if (appVersion) {
      headers['x-app-version'] = appVersion;
    }
    return headers;
  }

  addErrorInterceptor(interceptorFunc) {
    axios.interceptors.response.use((r) => r, interceptorFunc);
  }

  /**
   * @param {string} relativeUrl
   * @param {boolean} withToken
   * @returns {string}
   */
  generateAbsoluteUrl(relativeUrl, withToken = true) {
    const endpoint = `/${relativeUrl}`.replace(/\/+/g, '/');
    let url = `${this.apiBase}${endpoint}`;

    if (withToken && this.token) {
      url += `?${ApiClient.JWT_QUERY_TOKEN}=${this.token}`;
    }

    return url;
  }

  generateFileUrl(fileId) {
    return this.generateAbsoluteUrl(`/storage/file/${fileId}`);
  }

  /**
   * @param {*} args
   */
  static create(...args) {
    const client = new this(...args);

    axios.interceptors.request.use();

    axios.interceptors.request.use((config) => {
      if (window.app?.isApiLocal) {
        config.baseURL = 'http://localhost:3008';
      }
      return config;
    });

    return client.injectApiMethods(api);
  }

  /**
   * Static headers
   */
  static get STATIC_HEADERS() {
    return {
      'Content-Type': 'application/json'
    };
  }

  /**
   * @returns {string}
   */
  static get JWT_QUERY_TOKEN() {
    return 'jwtPulsarToken';
  }

  /**
   * @returns {string}
   */
  static get COGNITO_ACCESS_TOKEN() {
    return 'ReactAmplify.TokenKey';
  }

  /**
   * @return {String}
   */
  static get LS_KEY() {
    return '__token_dashboard__';
  }

  /**
   * @return {String}
   */
  static get ACTION_KEY() {
    return '__api_req__';
  }
}

export default ApiClient;
